import { useState } from 'react'
import { HiEye, HiEyeOff } from 'react-icons/hi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { formatNumber } from '../utils/helpers'

function ResultBanner({ tone, children }) {
  const tones = {
    ok:     'text-ok border-ok/25 bg-ok/5',
    warn:   'text-warn border-warn/25 bg-warn/5',
    danger: 'text-danger border-danger/25 bg-danger/5',
  }
  return (
    <p role="status" className={`px-3.5 py-2.5 rounded-lg border text-[13px] ${tones[tone]}`}>
      {children}
    </p>
  )
}

function BreachCheck() {
  const [password, setPassword]   = useState('')
  const [email, setEmail]         = useState('')
  const [pwResult, setPwResult]   = useState(null)
  const [emResult, setEmResult]   = useState(null)
  const [busy, setBusy]           = useState({ pw: false, em: false })
  const [show, setShow]           = useState(false)

  const checkPassword = async () => {
    if (!password.trim()) return
    setBusy(b => ({ ...b, pw: true }))
    try {
      const res = await api.post('/breach/check-password', { password })
      setPwResult(res.data)
    } catch (err) {
      const msg = err.response?.data?.error || 'Check failed — is the API running?'
      toast.error(msg)
    } finally {
      setBusy(b => ({ ...b, pw: false }))
    }
  }

  const checkEmail = async () => {
    if (!email.trim()) return
    setBusy(b => ({ ...b, em: true }))
    try {
      const res = await api.post('/breach/check-email', { email })
      setEmResult(res.data)
    } catch {
      toast.error('Check failed — is the API running?')
    } finally {
      setBusy(b => ({ ...b, em: false }))
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
      <header className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight mb-1.5">Breach check</h1>
        <p className="text-sm text-ink-muted">
          Looks up exposure against the HaveIBeenPwned corpus.
        </p>
      </header>

      {/* Password exposure */}
      <section className="card p-5 mb-5">
        <h2 className="eyebrow mb-1.5">Password</h2>
        <p className="text-xs text-ink-faint mb-4 leading-relaxed">
          Checked with k-anonymity: the password is hashed server-side and only the
          first 5 characters of the SHA-1 hash are sent to HIBP.
        </p>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setPwResult(null) }}
              onKeyDown={e => e.key === 'Enter' && checkPassword()}
              placeholder="Password to check"
              aria-label="Password to check"
              className="input !pr-11 font-mono"
              autoComplete="off"
              spellCheck="false"
            />
            <button
              type="button"
              onClick={() => setShow(v => !v)}
              aria-label={show ? 'Hide password' : 'Show password'}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-secondary transition-colors"
            >
              {show ? <HiEyeOff className="w-4 h-4" aria-hidden="true" /> : <HiEye className="w-4 h-4" aria-hidden="true" />}
            </button>
          </div>
          <button onClick={checkPassword} disabled={busy.pw || !password.trim()} className="btn-accent">
            {busy.pw ? 'Checking…' : 'Check'}
          </button>
        </div>

        {pwResult && (
          <div className="mt-4 space-y-3 animate-fade-in">
            {pwResult.error ? (
              <ResultBanner tone="warn">{pwResult.error}</ResultBanner>
            ) : pwResult.breached ? (
              <ResultBanner tone="danger">
                Seen {formatNumber(pwResult.count)} times in breach data. Don’t use this password anywhere.
              </ResultBanner>
            ) : (
              <ResultBanner tone="ok">Not found in any known breach.</ResultBanner>
            )}

            {pwResult.hash_info && (
              <div className="px-3.5 py-3 rounded-lg bg-surface-inset border border-surface-border">
                <p className="eyebrow mb-2">What HIBP received</p>
                <p className="font-mono text-xs break-all leading-relaxed">
                  <span className="text-accent">{pwResult.hash_info.hash_prefix}</span>
                  <span className="text-ink-faint">{pwResult.hash_info.hash_suffix}</span>
                </p>
                <p className="text-[11px] text-ink-faint mt-1.5">
                  Only the highlighted prefix was transmitted.
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Email exposure */}
      <section className="card p-5">
        <h2 className="eyebrow mb-4">Email</h2>

        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setEmResult(null) }}
            onKeyDown={e => e.key === 'Enter' && checkEmail()}
            placeholder="you@example.com"
            aria-label="Email address to check"
            className="input flex-1"
            spellCheck="false"
          />
          <button onClick={checkEmail} disabled={busy.em || !email.trim()} className="btn-accent">
            {busy.em ? 'Checking…' : 'Check'}
          </button>
        </div>

        {emResult && (
          <div className="mt-4 animate-fade-in">
            {emResult.error ? (
              <ResultBanner tone="warn">{emResult.error}</ResultBanner>
            ) : emResult.breached ? (
              <>
                <ResultBanner tone="danger">
                  Found in {emResult.count} breach{emResult.count === 1 ? '' : 'es'}.
                </ResultBanner>
                {emResult.breaches?.length > 0 && (
                  <ul className="mt-3 border border-surface-border rounded-lg overflow-hidden">
                    {emResult.breaches.slice(0, 8).map((b, i) => (
                      <li
                        key={b.name || i}
                        className="flex items-baseline justify-between px-3.5 py-2.5 text-[13px] border-b border-surface-border last:border-0"
                      >
                        <span className="text-ink font-medium">{b.title || b.name}</span>
                        {b.breach_date && (
                          <span className="font-mono text-xs text-ink-faint">{b.breach_date}</span>
                        )}
                      </li>
                    ))}
                    {emResult.breaches.length > 8 && (
                      <li className="px-3.5 py-2 text-xs text-ink-faint">
                        +{emResult.breaches.length - 8} more
                      </li>
                    )}
                  </ul>
                )}
              </>
            ) : (
              <ResultBanner tone="ok">No breaches on record for this address.</ResultBanner>
            )}
          </div>
        )}
      </section>
    </div>
  )
}

export default BreachCheck
